import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IndexCard, Korebog } from '@store/models';
import { LuxonUtils, weeks } from '@shared';
import { environment } from 'environments/environment';
import { MileageLogContainerComponent } from '../mileage-log/mileage-log.container';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { TimetrackDispatchers } from '@store';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { LegacyService } from 'app/legacy/legacy.service';

interface NavigationTab {
    step: number;
    text: string;
    lev: number;
}

@Component({
    selector: 'app-timetrack-modal-ui',
    templateUrl: './timetrack-modal.component.html',
    styleUrls: ['./timetrack-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TimetrackModalComponent implements OnChanges {

    @Input() user: IndexCard;
    @Input() lev: number;
    @Input() step: number;
    @Input() users: IndexCard[];
    @Input() selectedYear: number;
    @Input() selectedWeek: number;
    @Input() selectedDate: Date;
    @Input() searchFor: string;
    @Input() korebogPrint: Korebog[];
    @Output() selectStep = new EventEmitter<number>();
    @Output() selectUser = new EventEmitter<IndexCard>();
    @Output() selectYear = new EventEmitter<number>();
    @Output() selectWeek = new EventEmitter<number>();
    @Output() selectDate = new EventEmitter<Date>();
    @Output() selectToday = new EventEmitter();
    @Output() search = new EventEmitter<string>();

    @ViewChild(MileageLogContainerComponent) mileagLog: MileageLogContainerComponent;

    selectedDateForm: UntypedFormGroup;

    tabs: NavigationTab[] = [
        { step: 1, text: 'Ugerapport', lev: 0 },
        { step: 2, text: 'Dagsrapport (linjer)', lev: 0 },
        { step: 3, text: 'Kronekladde', lev: 0 },
        { step: 4, text: 'Kørsel', lev: 0 },
        { step: 5, text: 'Oversigt', lev: 0 }
    ];

    get years(): number[] {
        const firstYear = 2010;
        let lastYear = new Date().getFullYear() + 1;
        const periodArray: number[] = [];
        while (lastYear >= firstYear) {
            periodArray.push(lastYear--);
        }
        return periodArray;
    }

    get weeksInYear(): number[] {
        return weeks(this.selectedYear);
    }

    constructor(
        private activeModal: NgbActiveModal,
        private confirm: ConfirmService,
        private timetrackDispatch: TimetrackDispatchers,
        private fb: UntypedFormBuilder,
        private legacyService: LegacyService
    ) {
        this.selectedDateForm = this.fb.group({
            selectedDate: null
        });
        this.selectedDateForm.valueChanges.pipe(
            tap(() => this.onSelectDate())
        ).subscribe();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedDate) {
            this.selectedDateForm.patchValue({selectedDate: this.selectedDate});
        }
    }

    onDismiss() {
        this.activeModal.dismiss();
    }

    onSelectUser(user: IndexCard) {
        this.selectUser.emit(user);
    }

    onSelectYear(year: number) {
        const weeksInYear = LuxonUtils.fromObject({year}).weeksInLocalWeekYear;
        if (weeksInYear < this.selectedWeek) {
            this.selectWeek.emit(weeksInYear);
        }
        this.selectYear.emit(year);
    }

    onSelectWeek(week: number) {
        this.selectWeek.emit(week);
    }

    onWeekBack() {
        let newWeek = this.selectedWeek - 1;
        if (newWeek < 1) {
            const newYear = this.selectedYear - 1;
            this.selectYear.emit(newYear);
            newWeek = weeks(newYear).length;
        }
        this.selectWeek.emit(newWeek);
    }

    onWeekForward() {
        let newWeek = this.selectedWeek + 1;
        if (newWeek > weeks(this.selectedYear).length) {
            const newYear = this.selectedYear + 1;
            this.selectYear.emit(newYear);
            newWeek = 1;
        }
        this.selectWeek.emit(newWeek);
    }

    onToday() {
        this.selectToday.emit();
    }

    onSelectDate() {
        const date = LuxonUtils.utc(this.selectedDateForm.get('selectedDate').value).toJSDate();
        if (!LuxonUtils.fromIso(date).isValid) { return; }
        const selected = LuxonUtils.fromJSDate(this.selectedDate);
        const other = LuxonUtils.fromJSDate(date);
        if (selected.diff(other, 'days').days === 0) { return; }
        this.selectDate.emit(date);
    }

    gotoTab(tab: NavigationTab) {
        this.selectStep.emit(tab.step);
    }

    plannedTimeoff() {
        // TODO: change to new version when developed in settings
        const url = `${environment.ia3LegacyUrl}/timesag/normtid/angivfravaer.asp?medid=${this.user.cardId}`;
        const windowParams = 'scrollbars, resizable, toolbar, width=500, height=400, top=300';
        this.legacyService.ia3Navigate(url, 'hoponpop', windowParams);
    }

    onSearch(searchFor: string) {
        this.search.emit(searchFor);
    }

    pdfMileage() {
        this.mileagLog.pdfMileage();
    }

    paidoutMileage() {
        this.confirm.question(
            `Har du sendt listen til bogholderiet?`,
            'Ja',
            'Nej'
        ).then(yes => {
            if (yes) { this.timetrackDispatch.paidoutKorebog(); }
        });
    }

}
